import { ReactComponent as BackArrow } from "../../assets/arrow-back.svg";
import { ReactComponent as File } from "../../assets/file-small.svg";
import { ReactComponent as Clock } from "../../assets/clock.svg";
import { ReactComponent as Arrow } from "../../assets/arrow-back.svg";
import { ReactComponent as CyberPay } from "../../assets/cyberpay-logo.svg";
import styles from "./main.module.scss";
import Points from "../../assets/points.png";
import Button from "../../custom/button/button";
import { ErrorMessage, Field, FormikValues, useFormikContext } from "formik";
import { useQuery } from "@tanstack/react-query";
import {
  getAllBranches,
  getEventById,
  getEventScheduleByEventId,
  getMaterials,
  getRegisteredEvents,
} from "../../requests";
import { AxiosError } from "axios";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import classNames from "classnames";
import PayForOtherEvent from "./payForOtherEvent";
import { getDateWithoutTime } from "../../utils/formatDate";
import { Spin } from "antd";
import { useAtomValue, useSetAtom } from "jotai";
import { otherEventsStep, otherPayment, userData } from "../../state";
import { useEffect, useState } from "react";
import Select from "../../custom/select/select";
import PaymentPreviewModal from "../paymentPreviewModal/paymentPreviewModal";

const OtherEventDetails = ({
  isEventScheduled,
  setIsEventScheduled,
  isScheduling,
}: {
  isEventScheduled: boolean;
  setIsEventScheduled: React.Dispatch<React.SetStateAction<boolean>>;
  isScheduling: boolean;
}) => {
  const user = useAtomValue(userData);
  const [openPreview, setOpenPreview] = useState(false);
  const [params] = useSearchParams();
  const setStep = useSetAtom(otherEventsStep);
  const navigate = useNavigate();
  const { values, resetForm, submitForm, isValid, dirty } =
    useFormikContext<FormikValues>();
  const learningEventId: number = parseInt(values.learningEventId);
  const otherEventDetails = useAtomValue(otherPayment);
  const ref = params.get("ref");

  useEffect(() => {
    if (!!ref && otherEventDetails?.IsCorporateSponsor)
      setIsEventScheduled(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref]);

  const otherEventInfo = useQuery({
    queryKey: ["get-other-event-details"],
    queryFn: () => getEventById(learningEventId!),
    enabled: !!learningEventId,
  });
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["get-event-schedule"],
    queryFn: () => getEventScheduleByEventId(learningEventId!),
  });
  const materials = useQuery({
    queryKey: ["get-other-materials"],
    queryFn: () => getMaterials(learningEventId!),
    enabled: !!learningEventId,
  });
  const branches = useQuery({
    queryKey: ["get-branches"],
    queryFn: getAllBranches,
  });
  const registeredEvents = useQuery({
    queryKey: ["get-user-event-info"],
    queryFn: () => getRegisteredEvents(user?.MemberId!, "Other"),
  });

  const materialData = materials?.data?.Data as Material[];
  const otherEventInfoData = otherEventInfo?.data?.Data as EventById;
  const otherSchedule = data?.Data as EventSchedule[];
  const branchData = branches?.data?.Data;
  const registeredEventsData = registeredEvents?.data
    ?.Data as RegisteredEvent[];

  const userRegister = registeredEventsData?.find(
    (event) =>
      event.EventId === otherEventInfoData?.EventNameId &&
      event.PayingAmount > 0
  ) as RegisteredEvent;
  const hasUserRegistered = !!userRegister;
  const hasUserPaid = hasUserRegistered && userRegister?.HasPaid;

  const branchOptions = branchData?.map((branch) => (
    <option key={branch.BranchId} value={branch.BranchId}>
      {branch.BranchName}
    </option>
  ));

  const getBranchName = (branchId: number) =>
    branchData?.find((x) => x.BranchId === branchId)?.BranchName;
  const branchName = getBranchName(Number(values?.branch)) || "N/A";

  const getSchedule = (scheduleId: number) =>
    otherSchedule?.find((x) => x.TrainingScheduleId === scheduleId);
  const selectedSchedule =
    getSchedule(Number(values?.schedule)) || ({} as EventSchedule);

  let previewDetails = {
    eventTheme: otherEventInfoData?.EventCategoryName,
    eventName: otherEventInfoData?.EventName,
    location: branchName,
    organizationName: values?.organizationName,
    organizationAddress: values?.organizationAddress,
    organizationContactName: values?.contactPersonName,
    organizationContactEmail: values?.email,
    organizationContactPhone: values?.phoneNumber,
  };

  if (isLoading) return <Spin />;
  if (isError) return <div>{(error as AxiosError)?.message}</div>;

  return (
    <div>
      <section className={classNames("headingWithBackArrow")}>
        <button
          onClick={() => {
            resetForm({ values: { otherEventName: "", otherEventType: "" } });
            setStep((prev) => prev - 1);
          }}
        >
          <BackArrow />
        </button>
        <h3>Event Details</h3>
      </section>
      <section className={styles.detailsContainer}>
        <div className={styles.detailsContent}>
          <h2>
            {otherEventInfoData?.EventName}
            <span>
              <img src={Points} alt="points" />
            </span>
          </h2>
          <br />
          <div className={styles.line}></div>
          <br />
          {hasUserPaid && (
            <>
              <h3>Venue</h3>
              <p>{otherEventInfoData?.Venue || "N/A"}</p>
              <br />
            </>
          )}
          <h3>Business Case</h3>
          <p>{otherEventInfoData?.BusinessCase}</p>
          <br />
          <h3>Who to Participate</h3>
          <ul>
            <li>{otherEventInfoData?.TargetMember}</li>
          </ul>
          <br />
          <h3>Core Event Objectives</h3>
          <p>{otherEventInfoData?.CoreObjective}</p>
          <br />
          <h3>Talking Key Points</h3>
          <p>{otherEventInfoData?.DiscussionPoint}</p>
          <br />
          {materialData && materialData?.length > 0 ? (
            <h3>Event Schedule Material</h3>
          ) : null}
          <br />
          <section className={styles.list}>
            {materialData &&
              materialData?.map((item) => (
                <div key={item.MaterialId} className={styles.files}>
                  <div>
                    <File />
                    <label>{item.MaterialName}</label>
                  </div>

                  <Link
                    to={item.MaterialUrl}
                    target="_blank"
                    className={classNames("linkAsBtn")}
                  >
                    Download
                  </Link>
                </div>
              ))}
          </section>
        </div>

        <section className={styles.list}>
          {isEventScheduled || (hasUserRegistered && !hasUserPaid) ? (
            <>
              {values?.memberType === "corporate" &&
              otherEventInfoData?.IsPaymentRequired ? (
                <section className={styles.resource}>
                  <div>
                    <h3>Get Payment Breakdown</h3>
                    <p>Cost details of your preferred event listed here.</p>
                    <br />
                    <Button
                      onClick={() =>
                        navigate(
                          `/event-application/other-events/invoice?id=${
                            userRegister?.Id || otherEventDetails?.DataId
                          }&eventDetailId=${
                            otherEventInfoData?.LearningEventId
                          }`
                        )
                      }
                      text="Generate Invoice"
                      iconAfter={<Arrow />}
                    />
                  </div>
                  <CyberPay />
                </section>
              ) : (
                <PayForOtherEvent
                  amount={userRegister?.PayingAmount}
                  dataId={userRegister?.Id}
                  setIsEventScheduled={setIsEventScheduled}
                />
              )}
            </>
          ) : (
            <div className={styles.scheduleContainer}>
              <h3>Select preferred schedule</h3>
              {otherSchedule?.map((item) => {
                const startDate = getDateWithoutTime(item.StartDate);
                const endDate = getDateWithoutTime(item.EndDate);
                return (
                  <section
                    key={item.LearningEventId}
                    className={styles.eventSchedule}
                  >
                    <Clock />
                    <div>
                      <h4>{item?.Venue}</h4>
                      <label>
                        {startDate} - {endDate}
                      </label>
                      <label>{item.Time}</label>
                    </div>
                    <Field
                      type="radio"
                      name="schedule"
                      value={`${item?.TrainingScheduleId}`}
                    />
                  </section>
                );
              })}
              <ErrorMessage name="schedule">
                {(msg) => <div className={styles.error}>{msg}</div>}
              </ErrorMessage>
              <br />
              <div className={styles.list}>
                <Field
                  name="branch"
                  as={Select}
                  label="Location"
                  placeholder="Location"
                  options={branchOptions}
                />
              </div>
              <div className={styles.cta}>
                <Button
                  type="button"
                  onClick={() => setOpenPreview(true)}
                  text="Preview"
                  disabled={!isValid || !dirty || isScheduling}
                  isLoading={isScheduling}
                />
                {/* <Button
                  isLoading={isScheduling}
                  disabled={isScheduling}
                  type="submit"
                  text="Proceed to Payment"
                /> */}
              </div>
            </div>
          )}
        </section>
      </section>
      <PaymentPreviewModal
        open={openPreview}
        onClose={() => setOpenPreview(false)}
        submitForm={submitForm}
        eventType={"Event"}
        memberType={values?.memberType}
        eventDetails={previewDetails}
        schedule={selectedSchedule}
      />
    </div>
  );
};

export default OtherEventDetails;
